import { DateTime } from "luxon";
import { DayOfWeek } from "../Configuration/utils";
import { Task } from "../services/Task";
import { GenericObject, PriceUpdateStatus } from "../services/Price";
import { PriceRule } from "./PriceRule";

export type BulkPriceSetParams = {
  date_from: string;
  date_to: string;
  grid_types: Array<number>;
  price_rule: PriceRule;
  uh_cats?: Array<number> | undefined;
  uh_types?: Array<number> | undefined;
  property?: number;
  is_pending: boolean;
  is_ref: boolean;
  weekdays?: Array<number> | undefined;
};

export type BulkRestrictionsSetParams = {
  date_from: string;
  date_to: string;
  grid_types: Array<number>;
  // Scope: either explicit `uh_cats`, or `property` + `uh_types` (every active
  // cat of those UH types). The API refuses both at once.
  uh_cats?: Array<number>;
  property?: number;
  uh_types?: Array<number>;
  is_pending: boolean;
  weekdays?: Array<number> | undefined;
  // A missing key leaves existing restrictions untouched (partial update); an
  // explicit null clears it.
  restriction_rule: {
    closed_to_arrival?: boolean | null;
    closed_to_departure?: boolean | null;
    min_stay?: number | null;
    max_stay?: number | null;
  };
  rule?: RestrictionRule;
};

export type BulkPriceFormType = {
  grid_types: number[];
  is_pending: boolean;
  is_ref: boolean;
  rule: PriceRule;
  start: DateTime<true>;
  stop: DateTime<true>;
  uh_cats: UhCat[];
  uh_types: number[];
  uh_types_ref: number[];
  days: number[];
};

export type BulkRestrictionsFormType = {
  grid_types: number[];
  is_pending: boolean;
  start: DateTime<true>;
  stop: DateTime<true>;
  uh_cats: UhCat[];
  uh_types: number[];
  days: number[];
  max_stay?: string;
  min_stay?: string;
  closed_to_departure?: 0 | 1 | 2;
  closed_to_arrival?: 0 | 1 | 2;
};

export type MassEditFormProps = {
  gridTypes: number[];
  property: number;
  days: number[];
  dateStart: DateTime;
  onLoading: (isLoading: boolean) => void;
  onSubmit: (task: Task) => void;
  uhTypeNandcs: number[];
  uhCats: UhCat[];
  selectedUhCats: UhCat[];
};

interface ParamsQueryType {
  page: number;
  page_size: number;
  hebergement_uh_cat__capacity__occupation_date__gte: string;
  hebergement_uh_cat__capacity__occupation_date__lt: string;
  hebergement_uh_cat__property: number | null;
  occupation_date__gte: string;
  occupation_date__lte: string;
  ordering: string;
  weekdays: number[];
}

export type ParamsType = ParamsQueryType | GenericObject;

export interface UhTypeNandc {
  id: number;
  order: number | null;
  short_name: string;
}

interface KpiDate {
  id: number;
  date_equivalent: string;
  date_occupation: string;
  kpi: number;
  value: string;
}

export interface KpiUhCatDate extends KpiDate {
  uh_cat: number;
}

export interface KpiUhTypeNandcDate extends KpiDate {
  uh_type_nandc: number;
  property: number;
}

export interface UhCat {
  id: number;
  is_property_reference: boolean;
  is_uh_type_reference: boolean;
  order: number | null;
  property: number;
  short_name: string;
  uh_type_nandc: UhTypeNandc;
}

export interface Kpi {
  id: number;
  date_occupation: string;
  date_equivalent: string;
  value: string;
  kpi: number;
  uh_cat: number;
}

// orphan_night KPI: a row exists (value "1") only on dates flagged as an orphan
// night for the uh_cat — presence is the signal, no 0/null to interpret.
export const ORPHAN_NIGHT_KPI_ID = 302;

export interface KpiOccupationRateResquestParam {
  date_occupation__gte: string;
  date_occupation__lt: string;
  page_size: number;
  ordering: string;
  kpis: number;
  property: number;
  weekdays: number[];
}

export interface GridType {
  id: number;
  code: string;
  label: string;
  is_main: boolean;
}

// A grid type master and its grid types, as returned by `as_group`. The grid
// types without a master come last, under UNGROUPED_GRID_TYPE_GROUP_ID.
export interface GridTypeGroup {
  id: number;
  code: string;
  label: string;
  grids: GridType[];
}

export const UNGROUPED_GRID_TYPE_GROUP_ID = 0;

export interface Price {
  id: number;
  grid_type: GridType;
  uh_cat: UhCat;
  occupation_date: string;
  price_after_taxes: number;
}

export interface AlertType {
  id: number;
  action_type: string;
  action_value: number | null;
  criticity: number;
  date: string;
  is_active: boolean;
  nancie: string | null;
  price_reco: number | null;
  property: number;
  uh_type_nandc: number;
  // Where the two amounts of the recommendation sit on the grid driving the
  // room type: "3" on a tier, "3-4" between two, "10+" above the last. Null
  // when no grid drives the cell.
  tier_level_now?: string | null;
  tier_level_reco?: string | null;
  formattedDate: string;
}

export interface AlertPropertyType {
  id: number;
  date: string;
  property: number;
  formattedDate: string;
}

export interface PriceUpdateType {
  id: number;
  grid_type: GridType;
  uh_cat: UhCat;
}

export interface EventType {
  evenement: string;
  date_debut: string;
  date_fin: string;
  extra_attributes?: Record<string, unknown>;
  is_main: boolean;
  id: number;
}

export interface RestrictionsType {
  grid_type: number;
  id: number;
  uh_cat: number;
  closed_to_arrival: boolean | null;
  closed_to_departure: boolean | null;
  min_stay: number | null;
  max_stay: number | null;
  occupation_date: string;
}

export type DateDirectionType = "plus" | "minus" | "calendar";

export interface DayOfWeekType {
  id: number;
  checked: boolean;
  label: string;
  value: DayOfWeek;
}

export interface PropertyOptionType {
  id: number;
  hebergement_nom_court: string;
}

export enum GridSectionsName {
  PRICE = "TARIFS",
  CTA = "CTA",
  CTD = "CTD",
  MIN_LOS = "MIN_LOS",
  MAX_LOS = "MAX_LOS",
  YIELD_CLASS = "YIELD_CLASS",
  YIELD_CLASS_PARENT = "YIELD_CLASS_PARENT",
  QUOTA = "QUOTA",
}

export enum GridSectionsApiValues {
  PRICE = "price",
  CTA = "closed_to_arrival",
  CTD = "closed_to_departure",
  MIN_LOS = "min_stay",
  MAX_LOS = "max_stay",
  YIELD_CLASS = "open_close_yc",
  YIELD_CLASS_PARENT = "open_close_yc_uht",
  QUOTA = "quota",
}

export interface UhTypeNandcOptionType {
  id: number;
  short_name: string;
}

/**
 * Whether a restriction change goes through the server's min/max stay check
 * (`RestrictionRule` server-side): FORCE once the user confirmed it.
 */
export enum RestrictionRule {
  DEFAULT = "default",
  FORCE = "force",
}

export interface NewRestrictionsType {
  grid_type: number;
  uh_cat: number;
  occupation_date: string;
  closed_to_arrival?: number;
  closed_to_departure?: number;
  is_pending?: boolean;
  max_stay?: number;
  min_stay?: number;
  rule?: RestrictionRule;
}

/**
 * Usefull to get restriction value with restriction label
 */
export type RestrictionsEquivalenceLabelToValueMapType = {
  [key in Exclude<
    GridSectionsName,
    | GridSectionsName.PRICE
    | GridSectionsName.YIELD_CLASS
    | GridSectionsName.YIELD_CLASS_PARENT
    | GridSectionsName.QUOTA
  >]: Exclude<
    GridSectionsApiValues,
    | GridSectionsApiValues.YIELD_CLASS
    | GridSectionsApiValues.YIELD_CLASS_PARENT
    | GridSectionsApiValues.QUOTA
  >;
};

type OptionsDisplayTypeType = "kpi" | "section";

/**
 * Usefull to get pricingDecision section label with their api value
 */
export type RestrictionsEquivalenceValueToLabelMapType = {
  [key in Exclude<
    GridSectionsApiValues,
    | GridSectionsApiValues.YIELD_CLASS
    | GridSectionsApiValues.YIELD_CLASS_PARENT
    | GridSectionsApiValues.QUOTA
  >]: GridSectionsName;
};

/**
 * Usefull to get restriction kpi id with restriction label
 */
export type restrictionEquivalenceLabelToKpiIdMapType = {
  [key in Exclude<
    GridSectionsName,
    | GridSectionsName.PRICE
    | GridSectionsName.YIELD_CLASS
    | GridSectionsName.YIELD_CLASS_PARENT
    | GridSectionsName.QUOTA
  >]: number;
};

/**
 * Usefull to get restriction kpi id with restriction api label
 */
export type restrictionEquivalenceLabelApiToKpiIdMapType = {
  [key in Exclude<
    GridSectionsApiValues,
    | GridSectionsApiValues.PRICE
    | GridSectionsApiValues.YIELD_CLASS
    | GridSectionsApiValues.YIELD_CLASS_PARENT
    | GridSectionsApiValues.QUOTA
  >]: number;
};

export interface OptionsDisplayType {
  checked: boolean;
  disabled: boolean;
  isEnabledWithRestriction: boolean;
  kpiValue?: number;
  label: GridSectionsName | string;
  type: OptionsDisplayTypeType;
  value: string;
}

export enum GridPartType {
  KPI = "Kpi",
  OR = "OR",
  PRICING_DECISION = "pricingDecision",
  SEGMENTATION = "segmentation",
}

export interface RestrictionUpdateType {
  id: number;
  uh_cat: number;
  grid_type: number;
  occupation_date: string;
  type: Exclude<GridSectionsApiValues, GridSectionsApiValues.PRICE>;
  old: boolean | number | null;
  new: boolean | number | null;
  status: PriceUpdateStatus;
  updated_at: string;
  owner: {
    username: string;
  };
}

export type RefetchPopOverType =
  | "restrictions"
  | "prices"
  | "with-alerts"
  | "allPricingDecisions";

export interface BuyUpCascadedPrice {
  uh_cat_id: number;
  date: string;
  price: number;
}
